import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Aperture } from "lucide-react";
import { getPaymentStatus } from "@/lib/apiClient";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") return setStatus("paid");
        if (["expired", "failed"].includes(res.payment_status)) return setStatus("error");
      } catch {
        return setStatus("error");
      }
      if (++attempts >= 8) return setStatus("timeout");
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center bg-[#050505] px-6">
      <div className="max-w-md w-full text-center border border-white/10 bg-[#0f0f10] p-12">
        <Aperture className="w-8 h-8 text-[#D90429] mx-auto mb-8" />
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-[#EF233C] mx-auto mb-6 animate-spin" />
            <h1 className="font-head uppercase text-3xl text-white tracking-tight">Confirming Payment</h1>
            <p className="font-body text-gray-400 mt-3">Hold tight while we verify your booking...</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="font-head uppercase text-3xl text-white tracking-tight">Booking Confirmed</h1>
            <p className="font-body text-gray-400 mt-3">
              Payment received. A confirmation is on its way to your inbox — Darrel will be in touch shortly.
            </p>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-[#D90429] mx-auto mb-6" />
            <h1 className="font-head uppercase text-3xl text-white tracking-tight">
              {status === "timeout" ? "Still Processing" : "Something Went Wrong"}
            </h1>
            <p className="font-body text-gray-400 mt-3">
              {status === "timeout"
                ? "Your payment is taking a moment. Check your email shortly for confirmation."
                : "We couldn't confirm this payment. If you were charged, please reach out."}
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-home-link"
          className="inline-block mt-10 px-8 py-3 bg-[#D90429] hover:bg-[#EF233C] transition-colors text-white uppercase tracking-[0.15em] text-sm"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}
